/**
 * NOS-115 / NOS-178: Orchestrates site clone flow as an async import job.
 * initiateCrawl starts the job; this hook polls the import_jobs row until the
 * chained crawl + mapping workers finish, exposing live per-page progress.
 */
import { useState, useCallback, useRef, useEffect } from "react"
import { supabase } from "@/lib/supabase"
import { useOrgStore } from "@/stores/orgStore"
import {
  fetchImportJob,
  isImportJobActive,
  remainingSummaryFromJob,
  IMPORT_JOB_POLL_MS,
  type ImportJobRow,
  type ImportRemainingSummary,
} from "@/hooks/useImportJob"

/** NOS-192: "review" = the preview sample is imported, awaiting user approval. */
export type SiteCloneStatus = "idle" | "crawling" | "mapping" | "review" | "done" | "error"

/** Overall watchdog for the polling loop — jobs fail server-side well before this. */
const IMPORT_WATCHDOG_MS = 15 * 60_000

export interface CrawlPageMeta {
  url: string
  title: string
}

export interface ImportedPageSummary {
  slug: string
  section_count: number
  /** NOS-198: set when the mapper deliberately skipped this page (e.g. "blog_index"). */
  skipped?: string
}

export interface ImportJobProgress {
  status: string
  phase: string | null
  pagesFound: number
  pagesProcessed: number
  pagesOverflow: number
  /** NOS-192: "preview" while stage 1 (sample) runs; "full" for stage 2/legacy jobs */
  stage?: "preview" | "full"
}

/** NOS-192: what's on the table while the job is paused at the preview gate. */
export interface SiteCloneReviewInfo {
  jobId: string
  remaining: ImportRemainingSummary | null
}

export interface UseSiteCloneResult {
  status: SiteCloneStatus
  error: string | null
  crawlPages: CrawlPageMeta[]
  /** Live counts from the import_jobs row while a job runs */
  jobProgress: ImportJobProgress | null
  /** NOS-192: set while the job is paused at review (preview imported) */
  review: SiteCloneReviewInfo | null
  /** Starts the import job and resolves when it pauses at review or ends. */
  importSite: (
    url: string,
    orgId: string,
    opts?: ImportSiteOptions
  ) => Promise<{ ok: boolean; error?: string; pages: ImportedPageSummary[] }>
}

export interface ImportSiteOptions {
  /** NOS-184: per-job page cap; server honors it only for platform admins */
  pageCap?: number
  /** NOS-189: false = import content only (gate source design). Default = content + design. */
  importDesign?: boolean
}

function jobToProgress(job: ImportJobRow): ImportJobProgress {
  const gate = (job.state as { gate?: string } | null)?.gate
  return {
    status: job.status,
    phase: job.phase,
    pagesFound: job.pages_found,
    pagesProcessed: job.pages_processed,
    pagesOverflow: job.pages_overflow,
    stage: gate === "sample" ? "preview" : "full",
  }
}

function pagesFromJobResult(job: ImportJobRow): ImportedPageSummary[] {
  const raw = (
    job.result as {
      pages?: Array<{ slug?: string; section_count?: number; skipped?: string }>
    } | null
  )?.pages
  if (!Array.isArray(raw)) return []
  return raw
    .filter(
      (p): p is { slug: string; section_count: number; skipped?: string } =>
        typeof p?.slug === "string" && typeof p?.section_count === "number"
    )
    .map((p) => ({
      slug: p.slug,
      section_count: p.section_count,
      ...(typeof p.skipped === "string" && p.skipped ? { skipped: p.skipped } : {}),
    }))
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export function useSiteClone(): UseSiteCloneResult {
  const initiateCrawl = useOrgStore((s) => s.initiateCrawl)
  const [status, setStatus] = useState<SiteCloneStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [crawlPages, setCrawlPages] = useState<CrawlPageMeta[]>([])
  const [jobProgress, setJobProgress] = useState<ImportJobProgress | null>(null)
  const [review, setReview] = useState<SiteCloneReviewInfo | null>(null)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])

  const importSite = useCallback(
    async (
      url: string,
      orgId: string,
      opts?: ImportSiteOptions
    ): Promise<{ ok: boolean; error?: string; pages: ImportedPageSummary[] }> => {
      setError(null)
      setCrawlPages([])
      setJobProgress(null)
      setReview(null)
      setStatus("crawling")

      const started = await initiateCrawl(url, orgId, opts)
      if (!started.ok || !started.jobId) {
        const msg = started.error ?? "Crawl failed"
        setStatus("error")
        setError(msg)
        return { ok: false, error: msg, pages: [] }
      }

      const jobId = started.jobId
      const deadline = Date.now() + IMPORT_WATCHDOG_MS
      let crawlPagesLoaded = false
      let lastJob: ImportJobRow | null = null

      while (Date.now() < deadline) {
        const job = await fetchImportJob(jobId)
        if (unmountedRef.current) return { ok: false, error: "cancelled", pages: [] }

        if (job) {
          lastJob = job
          setJobProgress(jobToProgress(job))

          if (job.status === "crawling") {
            setStatus("crawling")
          } else if (job.status === "mapping") {
            setStatus("mapping")
            // Once the crawl completes, load page metadata for the per-page checklist
            if (!crawlPagesLoaded && job.crawl_result_id) {
              crawlPagesLoaded = true
              const { data: crawlMeta } = await supabase
                .from("site_crawl_results")
                .select("crawl_data")
                .eq("id", job.crawl_result_id)
                .single()
              const rawPages =
                (crawlMeta?.crawl_data as { pages?: Array<{ url: string; title?: string }> })
                  ?.pages ?? []
              if (!unmountedRef.current) {
                setCrawlPages(
                  rawPages.map((p, i) => ({
                    url: p.url ?? "",
                    title: p.title?.trim() || `Page ${i + 1}`,
                  }))
                )
              }
            }
          } else if (job.status === "failed") {
            const msg = job.error ?? "Import failed. Please try again."
            setStatus("error")
            setError(msg)
            return { ok: false, error: msg, pages: [] }
          } else if (job.status === "review") {
            // NOS-192: preview gate — the sample is imported; the user decides
            // whether stage 2 imports the rest.
            setStatus("review")
            setReview({ jobId: job.id, remaining: remainingSummaryFromJob(job) })
            return { ok: true, pages: pagesFromJobResult(job) }
          } else if (!isImportJobActive(job.status)) {
            setStatus("done")
            return { ok: true, pages: pagesFromJobResult(job) }
          }
        }

        await sleep(IMPORT_JOB_POLL_MS)
      }

      // Watchdog expired — treat as failure but leave the job row for debugging
      const msg = lastJob?.error ?? "Import timed out. Please try again."
      setStatus("error")
      setError(msg)
      return { ok: false, error: msg, pages: [] }
    },
    [initiateCrawl]
  )

  return { status, error, crawlPages, jobProgress, review, importSite }
}
